import { Document, Packer, Paragraph, HeadingLevel, TextRun, Table, TableRow, TableCell, WidthType, AlignmentType, BorderStyle } from "docx";
import { saveAs } from "file-saver";

const GOLD = "D4AF37";
const NAVY = "081426";

const H = (text, level = HeadingLevel.HEADING_1) =>
  new Paragraph({ heading: level, spacing: { before: 240, after: 120 }, children: [new TextRun({ text, bold: true, color: NAVY })] });

const P = (text, opts = {}) =>
  new Paragraph({ spacing: { after: 120 }, children: [new TextRun({ text, ...opts })] });

const bullet = (text) => new Paragraph({ bullet: { level: 0 }, spacing: { after: 60 }, children: [new TextRun(text)] });

const cell = (text, header = false) =>
  new TableCell({
    width: { size: 25, type: WidthType.PERCENTAGE },
    shading: header ? { fill: NAVY } : undefined,
    children: [new Paragraph({ children: [new TextRun({ text, bold: header, color: header ? "FFFFFF" : "000000", size: 18 })] })],
  });

function tierTable() {
  const rows = [
    ["Metric", "Starter", "Team", "Enterprise"],
    ["Price", "$4.99 / month", "$9.99 / month", "$29.99 / month"],
    ["Seats", "1 digital card", "5 user seats", "25 seats"],
    ["Branding", "Standard", "Custom logo & colors", "Full white-label + custom domain"],
    ["Leads", "Basic contact form", "Lead capture + CSV", "Lead capture + CRM webhooks"],
    ["Media", "Photo & logo", "Portfolio + products", "Unlimited + AI copywriter"],
    ["Team tools", "—", "Team directory", "Bulk import, audit log, self-registration"],
    ["AI Tools", "—", "—", "AI auto-fill & copywriter"],
  ];
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: rows.map((r, i) => new TableRow({ children: r.map((c) => cell(c, i === 0)) })),
  });
}

function build(title, subtitle, sections) {
  return new Document({
    sections: [{
      properties: {},
      children: [
        new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 60 }, children: [new TextRun({ text: "iWurks360DigitAL", bold: true, size: 40, color: GOLD })] }),
        new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 40 }, children: [new TextRun({ text: title, bold: true, size: 30, color: NAVY })] }),
        new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 300 }, children: [new TextRun({ text: subtitle, italics: true, color: "666666" })] }),
        ...sections,
      ],
    }],
  });
}

export async function downloadClientGuide() {
  const doc = build("Digital SmartCard — Comprehensive User Guide", "Features, Functionality & Client Benefits", [
    H("1. Executive Overview"),
    P("iWurks360 Digital is an enterprise-grade digital business card platform that replaces paper cards with contactless, interactive, always-up-to-date digital cards. Share instantly via QR code, NFC tap, or link — and update details live without ever reprinting a card."),
    H("2. Complete Feature Set"),
    bullet("Digital Card Builder with a real-time mobile preview — photo, logo, bio, contact details, social links, products and portfolio."),
    bullet("Public shareable card microsite for every card, with one-tap Save Contact (vCard .vcf) and Exchange Details lead capture."),
    bullet("AI Auto-Fill — paste a website or LinkedIn URL and Gemini AI builds the card for you."),
    bullet("AI Copywriter — generate polished professional bios, taglines and product descriptions."),
    bullet("QR & NFC Studio — export high-resolution QR codes and program physical NFC smart cards/badges."),
    bullet("Email Signature Generator — copy-paste branded HTML signatures for Gmail & Outlook."),
    bullet("Client Accounts & Seat Management — allocate seats to team members and enforce brand identity."),
    bullet("CSV Bulk Import with on-screen column mapping and one-click Undo."),
    bullet("Self-Registration Portal — employees create their own branded cards from an invite link (honeypot + rate-limit protected)."),
    bullet("Public Team Directory with search, department filter, and a downloadable printable QR poster per member."),
    bullet("Lead Capture Engine with CSV export and real-time CRM webhooks (Salesforce/HubSpot) with retry + failure alerts."),
    bullet("Custom Domain & White-Label — serve cards under your own branded web address with SSL."),
    bullet("Analytics — views, saves and leads funnel, 14-day trend, per-card and per-department breakdowns."),
    bullet("Audit Log & in-app Notifications for compliance and operational awareness."),
    H("3. Subscription Tiers & Feature Matrix"),
    tierTable(),
    new Paragraph({ spacing: { after: 120 }, children: [] }),
    H("4. Benefits Clients Achieve"),
    bullet("Never reprint a card again — update details once and every shared link, QR and NFC tag stays current."),
    bullet("Win more business — turn every interaction into a captured lead that flows straight into your CRM."),
    bullet("Protect your brand — enforce logos, colors and a custom domain across every employee card."),
    bullet("Onboard teams in minutes — bulk import or self-registration links instead of manual setup."),
    bullet("Measure what matters — see which people and departments network most and convert best."),
    bullet("Go greener and look sharper — a premium, contactless, sustainable alternative to paper."),
    H("5. Step-by-Step Client Onboarding"),
    P("Step 1 — Sign in and open Client Accounts from the dashboard sidebar."),
    P("Step 2 — Create a client account: set company name, plan, brand logo and primary color."),
    P("Step 3 — Add your team: allocate seats, bulk-import a CSV, or share the self-registration link."),
    P("Step 4 — Customize each card: contact details, bio, portfolio, products and CTAs."),
    P("Step 5 — Export QR codes, program NFC tags, and generate HTML email signatures in the Studio."),
    P("Step 6 — Connect a CRM webhook and export leads; monitor Analytics and the Audit Log."),
    H("6. Technical Support & Upgrades"),
    bullet("Upgrade a client plan anytime from the Client Accounts dashboard."),
    bullet("Configure a custom domain and CRM webhook per client account."),
    bullet("Contact your dedicated account manager for enterprise onboarding."),
  ]);
  const blob = await Packer.toBlob(doc);
  saveAs(blob, "iWurks360_Digital_Card_Client_User_Guide.docx");
}

export async function downloadMasterPrompt() {
  const doc = build("Master Prompt Script & Development Specification", "Full Build Blueprint — Start to Finish", [
    H("1. Executive Summary & Original Prompt"),
    P("Build an enterprise-grade digital business card and client account management platform (iWurks360 Digital). Replace paper cards with contactless, interactive digital cards featuring real-time updates, white-label branding, NFC programming, lead capture, QR codes, vCard downloads, email signatures, AI content generation and team seat allocation."),
    H("2. Technical Architecture & Stack"),
    bullet("Frontend: React 19 + React Router + Tailwind CSS + Framer Motion + Lucide icons."),
    bullet("Backend: FastAPI (Python) with JWT authentication."),
    bullet("Database: MongoDB (users, cards, client accounts, leads, transactions)."),
    bullet("Integrations: Gemini AI (auto-fill & copywriter), Stripe subscriptions, Emergent object storage."),
    bullet("Document generation: docx + file-saver for in-browser Word exports."),
    H("3. Core Functional Modules"),
    bullet("Card Builder with live mobile preview and public shareable card microsite."),
    bullet("Client Accounts portal with seat allocation & self-registration links."),
    bullet("QR & NFC Studio for vector QR export and simulated NFC tag writing."),
    bullet("Lead capture engine with CSV export."),
    bullet("HTML email signature generator."),
    bullet("Subscription tiers with Stripe checkout."),
    H("4. Subscription Tiers"),
    tierTable(),
    new Paragraph({ spacing: { after: 120 }, children: [] }),
    H("5. Addendum — Suggested Prompt Inputs"),
    bullet("Custom domain white-label: serve cards under cards.clientbrand.com."),
    bullet("CRM webhooks: push captured leads to Salesforce / HubSpot in real time."),
    bullet("Multi-language localization for global enterprise rollouts."),
    bullet("Analytics dashboard with scan heatmaps and conversion funnels."),
  ]);
  const blob = await Packer.toBlob(doc);
  saveAs(blob, "iWurks360_Master_Prompt_Script.docx");
}
